import type { FormEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert } from "@/atoms";
import { AuthHeader, AuthSuccessState, ForgotPasswordForm } from "@/components/auth";
import { routes, strings } from "@/constants";
import { AlertVariantEnum, ButtonVariantEnum } from "@/enums";
import { useForgotPassword } from "@/hooks";
import { AuthLayout } from "@/layouts";
import { validateEmailHandler } from "@/utils";

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState("");

    const [validationError, setValidationError] = useState("");

    const [isSuccess, setIsSuccess] = useState(false);

    const navigate = useNavigate();

    const {
        error: serviceError,
        isLoading,
        onRun: runForgotPasswordHandler,
    } = useForgotPassword();

    const error = validationError || serviceError || "";

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setValidationError("");

        const validation = validateEmailHandler(email);

        if (!validation.isValid) {
            setValidationError(validation.error || strings.errors.checkInput);

            return;
        }

        try {
            await runForgotPasswordHandler({ email });

            setIsSuccess(true);
        } catch {
            // Error already captured by hook
        }
    };

    const backToLoginHandler = () => navigate(routes.login);

    if (isSuccess) {
        return (
            <AuthLayout
                description={strings.forgotPassword.sidebarDescription}
                title={strings.forgotPassword.sidebarTitle}
            >
                <AuthSuccessState
                    actionLabel={strings.forgotPassword.backToLogin}
                    message={strings.forgotPassword.successMessage}
                    title={strings.forgotPassword.title}
                    variant={ButtonVariantEnum.LINK}
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            description={strings.forgotPassword.sidebarDescription}
            title={strings.forgotPassword.sidebarTitle}
        >
            <div>
                <AuthHeader
                    description={strings.forgotPassword.description}
                    title={strings.forgotPassword.title}
                />
                {error && (
                    <Alert
                        className="mb-6"
                        variant={AlertVariantEnum.ERROR}
                        onClose={() => setValidationError("")}
                    >
                        {error}
                    </Alert>
                )}
                <ForgotPasswordForm
                    email={email}
                    error={error}
                    isLoading={isLoading}
                    onBackToLogin={backToLoginHandler}
                    onSubmit={submitHandler}
                    onChangeEmail={(value) => {
                        setEmail(value);

                        setValidationError("");
                    }}
                />
            </div>
        </AuthLayout>
    );
};
